import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useContext, useEffect, useState } from "react";
import DataContext from "../../../../context/DataContext";
import ApiRequestErrorHandler from "../../ApiRequestErrorHandler";
import useService from "../../../../hooks/useService";
import InlineEditModal from "./InlineEditModal";
import InlineEditActions from "./InlineEditActions";

export default function InlineEditPhone({
  index,
  activeWindow,
  provider,
  trigger,
  setTrigger,
  phoneTypeOptions,
  handleCancelEdit,
  removeFormFields,
  providerTypesActive
}) {
  const providerContactInfoRepository = useService(
    "repositories/provider-contact-info-repository"
  );

  const { pk: providerPk } = provider;
  const { display = "", contact_label = "", id: activeId } = activeWindow || {};

  const { userRoles } = useContext(DataContext);
  const [responseModal, setResponseModal] = useState(false);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [isError, setIsError] = useState(false);
  const [phone, setPhone] = useState(display);
  const [phoneType, setPhoneType] = useState(contact_label);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  const canAdd = userRoles.permissions.includes("provider.add_providercontactinfo");
  const canPatch = userRoles.permissions.includes("provider.change_providercontactinfo");
  const canRemove = userRoles.permissions.includes("provider.delete_providercontactinfo");

  useEffect(() => {
    if (display || contact_label) {
      setPhone(display);
      setPhoneType(contact_label);
    }
  }, [display, contact_label]);

  const handleError = (error) => {
    let res = error.response?.data || error;
    let errArr = ApiRequestErrorHandler(res);
    setIsError(true);
    setResponseModal(true);
    setResponseBreakdown(errArr);
  }

  const postNewPhone = async () => {
    if (!canAdd) return;
    setLoading(true);
    const postData = {
      provider: providerPk,
      display: phone,
      contact_type: 1,
      contact_label: phoneTypeOptions.find(option => option.label === phoneType).key,
      related_provider: providerPk
    };
    try {
      await providerContactInfoRepository.createRecord(postData);
      setResponseModal(true);
      setResponseBreakdown(`Provider Phone Added`);
      setIsError(false);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const patchExistingPhone = async () => {
    if (!canPatch || !activeId) return;
    setLoading(true);
    const patchData = {
      display: phone,
      contact_type: 1,
      contact_label: phoneTypeOptions.find(option => option.label === phoneType).key,
      related_provider: providerPk
    };
    try {
      await providerContactInfoRepository.patch(activeId, patchData);
      setResponseModal(true);
      setResponseBreakdown("Provider Phone Updated");
      setIsError(false);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteExistingPhone = async () => {
    if (!canRemove || !activeId) return;
    setLoading(true);
    try {
      await providerContactInfoRepository.delete(activeId);
      setResponseModal(true);
      setResponseBreakdown("Provider Phone Deleted");
      setIsError(false);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const resetModalState = (shouldClearForm = false) => {
    setLoading(false);
    setIsError(false);
    setResponseModal(false);
    setResponseBreakdown("");
    if (shouldClearForm) {
      clearFormValState(index);
      handleCancelEdit();
      setTrigger(!trigger);
    } else {
      setConfirmDelete(false);
      handleCancelEdit();
      setTrigger(!trigger);
    }
  };

  const clearFormValState = (index) => {
    setPhone("");
    setPhoneType("");
    removeFormFields(index);
  };

  return (
    <div className="flex items-center w-full my-2">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="flex w-full items-center">
          <FormControl className="md:w-1/2 w-full" size="small">
            <InputLabel id="phone-type-label">Phone Types</InputLabel>
            <Select
              labelId="phone-type-label"
              id="phone-type"
              value={phoneType}
              label="Phone Type"
              onChange={(e) => {
                setPhoneType(e.target.value);
              }}
            >
              {phoneTypeOptions.length &&
                  phoneTypeOptions
                    .filter(phone => !providerTypesActive.includes(phone.label) || phone.label === activeWindow?.contact_label)
                    .map((phone) => (
                      <MenuItem
                        value={phone.label}
                        key={phone.key}
                      >
                        {phone.label}
                      </MenuItem>
                  ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Phone"
            value={phone}
            className="w-full mx-auto text-black ml-2"
            onChange={(e) => setPhone(e.target.value)}
          />{" "}
        </div>
      </LocalizationProvider>

      <InlineEditActions
        onSave={() => !activeWindow ? postNewPhone() : patchExistingPhone()}
        onDelete={() => setConfirmDelete(true)}
        onCancel={() => !activeWindow ? clearFormValState(index) : handleCancelEdit()}
        loading={loading}
        canAdd={canAdd}
        canPatch={canPatch}
        canRemove={canRemove}
        activeWindow={activeWindow}
        title="phone"
      />

      <InlineEditModal
        isError={isError}
        responseModal={responseModal}
        setResponseModal={setResponseModal}
        responseBreakdown={responseBreakdown}
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        activeWindow={activeWindow}
        displayKey="contact_label"
        displayValue="display"
        onDelete={deleteExistingPhone}
        onClose={() => resetModalState(false)}
        onNewClose={() => resetModalState(true)}
        loading={loading}
        title="phone"
      />
    </div>
  );
}

InlineEditPhone.propTypes = {
  index: PropTypes.number.isRequired,
  activeWindow: PropTypes.shape({
    display: PropTypes.string,
    contact_label: PropTypes.string,
    id: PropTypes.number
  }),
  provider: PropTypes.shape({
    pk: PropTypes.number.isRequired
  }).isRequired,
  trigger: PropTypes.bool.isRequired,
  setTrigger: PropTypes.func.isRequired,
  phoneTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  handleCancelEdit: PropTypes.func.isRequired,
  removeFormFields: PropTypes.func.isRequired,
  providerTypesActive: PropTypes.arrayOf(PropTypes.string).isRequired
};

InlineEditPhone.defaultProps = {
  activeWindow: null
};
